(() => {
  if (window.OrliSearchReliability) return;
  const controllers = new Map();

  function attach(owner) {
    if (controllers.has(owner)) return;
    const panel = owner.querySelector('[data-popdown]');
    const details = owner.querySelector('details');
    const summary = details?.querySelector('summary');
    const input = panel?.querySelector('input[type="search"]');
    if (!panel || !details || !summary || !input) return;

    const listeners = new AbortController();
    const mobile = window.matchMedia('(max-width: 749px)');
    const scrollChildren = [...panel.querySelectorAll('[data-predictive-search-results], [data-popular-searches]')];
    let frame = 0;
    let wasOpen = false;
    let instantFocus = false;

    function scheduleSize() {
      if (frame) return;
      frame = requestAnimationFrame(updateSize);
    }

    function updateSize() {
      frame = 0;
      if (!owner.classList.contains('is-open')) return;
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      const available = Math.max(0, Math.floor(bottom - panel.getBoundingClientRect().top));
      panel.style.setProperty('--orli-search-available-height', available + 'px');
      for (const child of scrollChildren) {
        const childHeight = Math.max(0, Math.floor(bottom - child.getBoundingClientRect().top));
        child.style.setProperty('--orli-search-results-height', childHeight + 'px');
      }
    }

    function syncScrollContainers() {
      for (const child of scrollChildren) child.toggleAttribute('data-scroll-lock-scrollable', !mobile.matches);
      scheduleSize();
    }

    function hasInstantTransition() {
      return getComputedStyle(panel).transitionDuration.split(',').every(value => !parseFloat(value));
    }

    function syncOpenState() {
      const open = owner.classList.contains('is-open');
      if (open && !details.open) {
        owner.close();
        return;
      }
      if (open !== wasOpen) {
        wasOpen = open;
        if (open) {
          instantFocus = hasInstantTransition();
          if (instantFocus) input.focus({preventScroll: true});
        } else if (instantFocus || hasInstantTransition()) {
          const focusWasInside = panel.contains(document.activeElement);
          details.removeAttribute('open');
          if (!instantFocus && focusWasInside) owner.a11y?.removeTrapFocus();
          instantFocus = false;
          if (focusWasInside) summary.focus({preventScroll: true});
        }
      }
      scheduleSize();
    }

    function preserveKeyboardDismissal(event) {
      const next = event.relatedTarget;
      if (event.target === input && input.value.trim() && owner.classList.contains('is-open') &&
          (!next || next === document.body || next === document.documentElement)) {
        event.stopPropagation();
      }
    }

    function trapInstantTab(event) {
      if (!instantFocus || !owner.classList.contains('is-open') || event.key !== 'Tab') return;
      const candidates = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')];
      const focusable = candidates.filter(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && (event.target === first || event.target === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && event.target === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function markPointerFocus() {
      summary.setAttribute('data-orli-pointer-focus', '');
    }

    function restoreKeyboardFocus(event) {
      if (['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape', ' '].includes(event.key)) {
        summary.removeAttribute('data-orli-pointer-focus');
      }
    }

    owner.addEventListener('pointerdown', markPointerFocus, {capture: true, signal: listeners.signal});
    document.addEventListener('keydown', restoreKeyboardFocus, {capture: true, signal: listeners.signal});
    owner.addEventListener('focusout', preserveKeyboardDismissal, {capture: true, signal: listeners.signal});
    owner.addEventListener('keydown', trapInstantTab, {signal: listeners.signal});
    panel.addEventListener('transitionend', scheduleSize, {signal: listeners.signal});
    window.addEventListener('resize', scheduleSize, {signal: listeners.signal});
    window.visualViewport?.addEventListener('resize', scheduleSize, {signal: listeners.signal});
    window.visualViewport?.addEventListener('scroll', scheduleSize, {signal: listeners.signal});
    mobile.addEventListener('change', syncScrollContainers);
    const stateObserver = new MutationObserver(syncOpenState);
    stateObserver.observe(owner, {attributes: true, attributeFilter: ['class']});
    stateObserver.observe(details, {attributes: true, attributeFilter: ['open']});
    const resultObserver = new MutationObserver(scheduleSize);
    for (const child of scrollChildren) resultObserver.observe(child, {childList: true, subtree: true});
    const sizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleSize) : null;
    const main = panel.querySelector('.search-popdown__main');
    if (main) sizeObserver?.observe(main);
    controllers.set(owner, () => {
      cancelAnimationFrame(frame);
      listeners.abort();
      mobile.removeEventListener('change', syncScrollContainers);
      stateObserver.disconnect();
      resultObserver.disconnect();
      sizeObserver?.disconnect();
      controllers.delete(owner);
    });
    syncScrollContainers();
    syncOpenState();
  }

  function init(root = document) {
    if (root.matches?.('header-search-popdown')) attach(root);
    root.querySelectorAll('header-search-popdown').forEach(attach);
  }

  window.OrliSearchReliability = {init};
  customElements.whenDefined('header-search-popdown').then(() => init());
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [owner, cleanup] of controllers) if (event.target.contains(owner)) cleanup();
  });
})();
